import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarOff, Trash2, Plus } from "lucide-react";

const isAdmin = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; }
};

// Admin-only: holiday list skipped (like Sundays) by "Today + Yesterday".
export const HolidaysButton = ({ testId = "" }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [d, setD] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/settings/holidays`, { headers: getAuthHeader() })
      .then(r => setList(r.data.holidays || [])).catch(() => toast.error("Could not load holidays"));
  }, [open]);
  if (!isAdmin()) return null;

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/settings/holidays`, { holidays: next }, { headers: getAuthHeader() });
      setList(r.data.holidays);
      window.dispatchEvent(new Event("holidays-changed"));
      toast.success("Holidays saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const add = () => {
    if (!d) return;
    save([...list.filter(h => h.date !== d), { date: d, label: label || "Holiday" }]);
    setD(""); setLabel("");
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Holidays — skipped like Sundays in Today + Yesterday"
        data-testid={`holidays-btn${testId ? `-${testId}` : ""}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-white text-[10px] text-emerald-700 hover:bg-emerald-100">
        <CalendarOff className="w-3 h-3" /> Holidays
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()} data-testid="holidays-dialog">
          <DialogHeader>
            <DialogTitle>Holidays</DialogTitle>
            <DialogDescription>
              Sundays and these dates are not working days. On the next working day, "Today + Yesterday"
              reaches back to the previous working day and also includes the skipped days.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="h-8 w-40" data-testid="holiday-date-input" />
            <Input placeholder="Label e.g. Diwali" value={label} onChange={(e) => setLabel(e.target.value)} className="h-8" data-testid="holiday-label-input" />
            <Button size="sm" className="h-8" onClick={add} disabled={!d || busy} data-testid="holiday-add-btn"><Plus className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="max-h-64 overflow-auto divide-y border rounded" data-testid="holiday-list">
            {list.map((h) => (
              <div key={h.date} className={`flex items-center justify-between px-2 py-1.5 text-sm ${h.date < today ? "text-slate-400" : ""}`}
                   data-testid={`holiday-row-${h.date}`}>
                <span><span className="font-mono">{h.date}</span> · {h.label}</span>
                <button onClick={() => save(list.filter(x => x.date !== h.date))} disabled={busy}
                  className="text-slate-400 hover:text-red-600" data-testid={`holiday-remove-${h.date}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {!list.length && <div className="p-4 text-center text-slate-400 text-sm">No holidays yet — Sundays are always skipped</div>}
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="holidays-close">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
